import { GET_REGIONAL_SIGNUP_MAP } from '@/lists/regionalSignup';
import { Region } from '@heidi/schema/ml-scribe';

const EU_COUNTRIES = [
  'Austria',
  'Belgium',
  'Bulgaria',
  'Croatia',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Estonia',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'Hungary',
  'Ireland',
  'Italy',
  'Latvia',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Netherlands',
  'Poland',
  'Portugal',
  'Romania',
  'Slovakia',
  'Slovenia',
  'Spain',
  'Sweden',
  // Sweden's Azure (SE-AZ1) enterprise variant is still geographically EEA,
  // so it must be treated as EEA for evidence regional gating.
  'Sweden (Enterprise)',
] as const;

const EEA_ONLY_COUNTRIES = ['Iceland', 'Liechtenstein', 'Norway'] as const;

export type EuCountry = (typeof EU_COUNTRIES)[number];
export type EeaCountry = EuCountry | (typeof EEA_ONLY_COUNTRIES)[number];

export function getEuCountries(): readonly EuCountry[] {
  return EU_COUNTRIES;
}

export function getEeaCountries(): readonly EeaCountry[] {
  return [...EU_COUNTRIES, ...EEA_ONLY_COUNTRIES];
}

export function isEuCountry(country: string): country is EuCountry {
  return (EU_COUNTRIES as readonly string[]).includes(country);
}

export function isEeaCountry(country: string): country is EeaCountry {
  return (
    (EU_COUNTRIES as readonly string[]).includes(country) ||
    (EEA_ONLY_COUNTRIES as readonly string[]).includes(country)
  );
}

const BASE_COUNTRIES_TO_REGION: { [key: string]: Region } = {
  Afghanistan: Region.EU,
  Albania: Region.EU,
  Algeria: Region.EU,
  'American Samoa': Region.US,
  Andorra: Region.EU,
  Angola: Region.EU,
  Anguilla: Region.US,
  Antarctica: Region.AU,
  'Antigua and Barbuda': Region.US,
  Argentina: Region.US,
  Armenia: Region.EU,
  Aruba: Region.US,
  Australia: Region.AU,
  // Enterprise (AZ1) country variants are interleaved alphabetically below
  // (e.g. `Japan (Enterprise)`, `United Arab Emirates (Enterprise)`). They
  // exist only in the data layer — `team.country` values persisted by the
  // backend on AZ1 stacks — so the region mismatch check in the join-team
  // flow resolves correctly. They are intentionally NOT added to
  // `lists/countries.ts`, so they never appear in any user-facing
  // CountrySelect dropdown. The regular per-country mappings below (e.g. Japan →
  // AU, `United Arab Emirates` → EU) are the historical fallbacks; they are
  // overridden to a regional stack by the `config-regional-signup` JSON
  // flag at the proxy below.
  'Australia (Enterprise)': Region.AU_AZ1,
  Austria: Region.EU,
  Azerbaijan: Region.EU,
  Bahamas: Region.US,
  Bahrain: Region.EU,
  Bangladesh: Region.AU,
  Barbados: Region.US,
  Belarus: Region.EU,
  Belgium: Region.EU,
  Belize: Region.US,
  Benin: Region.EU,
  Bermuda: Region.US,
  Bhutan: Region.AU,
  Bolivia: Region.US,
  'Bosnia and Herzegovina': Region.EU,
  Botswana: Region.EU,
  Brazil: Region.US,
  'British Indian Ocean Territory': Region.UK,
  'Brunei Darussalam': Region.AU,
  Bulgaria: Region.EU,
  'Burkina Faso': Region.EU,
  Burundi: Region.EU,
  Cambodia: Region.AU,
  Cameroon: Region.EU,
  Canada: Region.CA,
  'Cape Verde': Region.EU,
  'Cayman Islands': Region.US,
  'Central African Republic': Region.EU,
  Chad: Region.EU,
  Chile: Region.US,
  China: Region.AU,
  'Christmas Island': Region.AU,
  'Cocos (Keeling) Islands': Region.AU,
  Colombia: Region.US,
  Comoros: Region.EU,
  'Congo, Democratic Republic of the': Region.EU,
  'Congo, Republic of the': Region.EU,
  'Cook Islands': Region.AU,
  'Costa Rica': Region.US,
  "Cote d'Ivoire": Region.EU,
  Croatia: Region.EU,
  Cuba: Region.US,
  Curacao: Region.US,
  Cyprus: Region.EU,
  'Czech Republic': Region.EU,
  Denmark: Region.EU,
  Djibouti: Region.EU,
  Dominica: Region.US,
  'Dominican Republic': Region.US,
  Ecuador: Region.US,
  Egypt: Region.EU,
  'El Salvador': Region.US,
  'Equatorial Guinea': Region.EU,
  Eritrea: Region.EU,
  Estonia: Region.EU,
  Ethiopia: Region.EU,
  'Falkland Islands (Malvinas)': Region.US,
  'Faroe Islands': Region.EU,
  Fiji: Region.AU,
  Finland: Region.EU,
  France: Region.EU,
  'French Guiana': Region.US,
  'French Southern Territories': Region.AU,
  Gabon: Region.EU,
  Gambia: Region.EU,
  Georgia: Region.EU,
  Germany: Region.EU,
  Ghana: Region.EU,
  Gibraltar: Region.EU,
  Greece: Region.EU,
  Greenland: Region.EU,
  Grenada: Region.US,
  Guadeloupe: Region.US,
  Guam: Region.AU,
  Guatemala: Region.US,
  Guernsey: Region.EU,
  Guinea: Region.EU,
  'Guinea-Bissau': Region.EU,
  Guyana: Region.US,
  Haiti: Region.US,
  'Holy See (Vatican City State)': Region.EU,
  Honduras: Region.US,
  'Hong Kong': Region.AU,
  Hungary: Region.EU,
  Iceland: Region.EU,
  India: Region.AU,
  Indonesia: Region.AU,
  'Iran, Islamic Republic of': Region.EU,
  Iraq: Region.EU,
  Ireland: Region.EU,
  'Isle of Man': Region.EU,
  Israel: Region.EU,
  Italy: Region.EU,
  Jamaica: Region.US,
  Japan: Region.AU,
  'Japan (Enterprise)': Region.JP_AZ1,
  Jersey: Region.EU,
  Jordan: Region.EU,
  Kazakhstan: Region.EU,
  Kenya: Region.EU,
  Kiribati: Region.AU,
  "Korea, Democratic People's Republic of": Region.AU,
  'Korea, Republic of': Region.AU,
  Kuwait: Region.EU,
  Kyrgyzstan: Region.EU,
  "Lao People's Democratic Republic": Region.AU,
  Latvia: Region.EU,
  Lebanon: Region.EU,
  Lesotho: Region.EU,
  Liberia: Region.EU,
  Libya: Region.EU,
  Liechtenstein: Region.EU,
  Lithuania: Region.EU,
  Luxembourg: Region.EU,
  Macao: Region.AU,
  'Macedonia, the Former Yugoslav Republic of': Region.EU,
  Madagascar: Region.EU,
  Malawi: Region.EU,
  Malaysia: Region.AU,
  Maldives: Region.AU,
  Mali: Region.EU,
  Malta: Region.EU,
  'Marshall Islands': Region.AU,
  Martinique: Region.US,
  Mauritania: Region.EU,
  Mauritius: Region.EU,
  Mayotte: Region.EU,
  Mexico: Region.US,
  'Micronesia, Federated States of': Region.AU,
  'Moldova, Republic of': Region.EU,
  Monaco: Region.EU,
  Mongolia: Region.AU,
  Montenegro: Region.EU,
  Montserrat: Region.US,
  Morocco: Region.EU,
  Mozambique: Region.EU,
  Myanmar: Region.AU,
  Namibia: Region.EU,
  Nauru: Region.AU,
  Nepal: Region.AU,
  Netherlands: Region.EU,
  'New Caledonia': Region.AU,
  'New Zealand': Region.AU,
  Nicaragua: Region.US,
  Niger: Region.EU,
  Nigeria: Region.EU,
  Niue: Region.AU,
  'Norfolk Island': Region.AU,
  'Northern Mariana Islands': Region.AU,
  Norway: Region.EU,
  Oman: Region.EU,
  Pakistan: Region.AU,
  Palau: Region.AU,
  'Palestine, State of': Region.EU,
  Panama: Region.US,
  'Papua New Guinea': Region.AU,
  Paraguay: Region.US,
  Peru: Region.US,
  Philippines: Region.AU,
  Pitcairn: Region.AU,
  Poland: Region.EU,
  Portugal: Region.EU,
  'Puerto Rico': Region.US,
  Qatar: Region.EU,
  'Qatar (Enterprise)': Region.QAT_AZ1,
  Reunion: Region.EU,
  Romania: Region.EU,
  'Russian Federation': Region.EU,
  Rwanda: Region.EU,
  'Saint Barthelemy': Region.US,
  'Saint Helena': Region.EU,
  'Saint Kitts and Nevis': Region.US,
  'Saint Lucia': Region.US,
  'Saint Martin (French part)': Region.US,
  'Saint Pierre and Miquelon': Region.US,
  'Saint Vincent and the Grenadines': Region.US,
  Samoa: Region.AU,
  'San Marino': Region.EU,
  'Sao Tome and Principe': Region.EU,
  'Saudi Arabia': Region.EU,
  Senegal: Region.EU,
  Serbia: Region.EU,
  Seychelles: Region.EU,
  'Sierra Leone': Region.EU,
  Singapore: Region.AU,
  'Sint Maarten (Dutch part)': Region.US,
  Slovakia: Region.EU,
  Slovenia: Region.EU,
  'Solomon Islands': Region.AU,
  Somalia: Region.EU,
  'South Africa': Region.EU,
  'South Georgia and the South Sandwich Islands': Region.AU,
  'South Sudan': Region.EU,
  Spain: Region.EU,
  'Sri Lanka': Region.AU,
  Sudan: Region.EU,
  Suriname: Region.US,
  'Svalbard and Jan Mayen': Region.EU,
  Swaziland: Region.EU,
  Sweden: Region.EU,
  'Sweden (Enterprise)': Region.SE_AZ1,
  Switzerland: Region.EU,
  'Syrian Arab Republic': Region.EU,
  'Taiwan, Province of China': Region.AU,
  Tajikistan: Region.EU,
  'Tanzania, United Republic of': Region.EU,
  Thailand: Region.AU,
  'Timor-Leste': Region.AU,
  Togo: Region.EU,
  Tokelau: Region.AU,
  Tonga: Region.AU,
  'Trinidad and Tobago': Region.US,
  Tunisia: Region.EU,
  Turkey: Region.EU,
  Turkmenistan: Region.EU,
  'Turks and Caicos Islands': Region.US,
  Tuvalu: Region.AU,
  Uganda: Region.EU,
  Ukraine: Region.EU,
  'United Arab Emirates': Region.EU,
  'United Arab Emirates (Enterprise)': Region.UAE_AZ1,
  'United Kingdom': Region.UK,
  'United States': Region.US,
  'United States (Enterprise)': Region.US_AZ1,
  Uruguay: Region.US,
  Uzbekistan: Region.EU,
  Vanuatu: Region.AU,
  Venezuela: Region.US,
  Vietnam: Region.AU,
  'Virgin Islands, British': Region.UK,
  'Virgin Islands, U.S.': Region.US,
  'Wallis and Futuna': Region.AU,
  'Western Sahara': Region.EU,
  Yemen: Region.EU,
  Zambia: Region.EU,
  Zimbabwe: Region.EU,
  Other: Region.AU,
};

/**
 * Regional signup routing (Flagsmith JSON flag `config-regional-signup`).
 *
 * When the flag is on, a country present in its payload (e.g.
 * `{ "Japan": "jp-az1", "United Arab Emirates": "uae-az1" }`) resolves to the
 * mapped regional stack instead of its historical fallback in the static map
 * above. This is implemented as a Proxy so every consumer that indexes
 * `countriesToRegion[country]` — signup region writes, the sign-up subdomain
 * redirect, and analytics `region` traits — picks up the override from a single
 * chokepoint, with no per-call-site changes.
 *
 * `(Enterprise)` keys are separate entries that already resolve to their `*_AZ1`
 * region and are therefore unaffected. When the flag is off (or unevaluated,
 * e.g. on-prem where Flagsmith never initialises, or before the OpenFeature
 * provider is ready) {@link GET_REGIONAL_SIGNUP_MAP} returns `{}` and the lookup
 * falls through to the static fallback value.
 */
export const countriesToRegion: { [key: string]: Region } = new Proxy(
  BASE_COUNTRIES_TO_REGION,
  {
    get(target, prop, receiver) {
      if (typeof prop === 'string') {
        const override = GET_REGIONAL_SIGNUP_MAP()[prop];
        if (override) return override;
      }
      return Reflect.get(target, prop, receiver) as Region | undefined;
    },
  }
);

/** Whether `countryName` belongs to `region`; the comparison ignores case. */
export function isCountryInRegion(countryName: string, region: string): boolean {
  return countriesToRegion[countryName]?.toUpperCase() === region.toUpperCase();
}

const KNOWN_REGIONS: ReadonlySet<string> = new Set(
  Object.values(Region).map((region) => region.toUpperCase())
);

/** Whether `region` is one of the `Region` values; the comparison ignores case. */
export function isKnownRegion(region: string): boolean {
  return KNOWN_REGIONS.has(region.toUpperCase());
}

/**
 * Whether `region` is a known enterprise (`-AZ1`) region, whose countries are
 * the enterprise variants rather than the default list. Unknown regions are
 * not enterprise ones: the extras must follow the same rule as the filter.
 */
export function isEnterpriseRegion(region: string | null | undefined): boolean {
  return !!region && isKnownRegion(region) && region.toUpperCase().endsWith('-AZ1');
}

/**
 * The countries that belong to `region`. A known region returns exactly its
 * countries, which is none when the list holds none of them (an enterprise
 * region against the default list). A region that is unknown, or none given,
 * returns the list unchanged, so a bad region never empties a picker.
 */
export function filterCountriesByRegion<T extends { name: string }>(
  countries: readonly T[],
  region: string | null | undefined
): T[] {
  if (!region || !isKnownRegion(region)) return [...countries];
  return countries.filter((country) => isCountryInRegion(country.name, region));
}
